import { Head, Link, router } from "@inertiajs/react"
import { CheckCircle2, Flag, Mail, RotateCcw } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"

type Report = {
  id: number
  status: "open" | "resolved"
  message: string
  email: string | null
  page_path: string
  locale: string
  created_at: string
  resolved_at: string | null
  content_type: string
  content_name: string
  admin_path: string
}

export default function ProblemReportIndex({ reports, summary }: { reports: Report[]; summary: { open: number; resolved: number } }) {
  return (
    <>
      <Head title="Problem reports" />
      <AdminShell>
        <header>
          <p className="public-kicker">Visitor freshness feedback</p>
          <h1 className="mt-2">Problem reports</h1>
          <p className="mt-2 max-w-2xl">Open reports come first. Resolve them after updating or verifying the referenced content; resolved history stays available here.</p>
        </header>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <Summary icon={Flag} label="Open" value={summary.open} signal />
          <Summary icon={CheckCircle2} label="Resolved" value={summary.resolved} />
        </div>
        <div className="mt-7 space-y-4">
          {reports.length > 0 ? reports.map((report) => (
            <article key={report.id} className="rounded-xl border border-hairline bg-surface p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <Badge tone={report.status === "open" ? "signal" : "muted"}>{report.status}</Badge>
                  <h2 className="mt-2 text-lg"><Link href={report.admin_path}>{report.content_name}</Link></h2>
                  <p className="mt-1 text-xs text-ink-muted">{report.content_type} · {report.locale.toUpperCase()} · {new Date(report.created_at).toLocaleString()}</p>
                </div>
                <button type="button" onClick={() => router.patch(`/admin/problem_reports/${report.id}`, { status: report.status === "open" ? "resolved" : "open" })} className="btn btn-secondary">
                  {report.status === "open" ? <CheckCircle2 className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
                  {report.status === "open" ? "Resolve" : "Reopen"}
                </button>
              </div>
              <p className="mt-4 whitespace-pre-wrap text-ink-body">{report.message}</p>
              <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 border-t border-hairline pt-3 text-sm text-ink-muted">
                <a href={report.page_path}>{report.page_path}</a>
                {report.email && <a href={`mailto:${report.email}`} className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{report.email}</a>}
              </div>
            </article>
          )) : <p className="rounded-xl border border-hairline bg-surface p-8 text-center text-ink-muted">No problem reports yet.</p>}
        </div>
      </AdminShell>
    </>
  )
}

function Summary({ icon: Icon, label, value, signal = false }: { icon: typeof Flag; label: string; value: number; signal?: boolean }) {
  return <div className="rounded-xl border border-hairline bg-surface p-4"><Icon className={`h-5 w-5 ${signal ? "text-signal" : "text-accent"}`} /><strong className="mt-3 block text-2xl">{value}</strong><span className="text-sm text-ink-muted">{label}</span></div>
}
